import type { RequestTiming } from '../api/httpClient'
import type { KeyValueStorage } from '../persistence/storage'
import { isTraceRoute, routeTemplateOf } from './routeTemplate'
import {
  MAX_DETAIL_PER_REQUEST,
  MAX_POINTS_PER_REQUEST,
  type TraceDelivery,
  type TraceDetailBatch,
  type TraceDetailEvent,
  type TraceDetailKind,
  type TraceMeta,
  type TraceNetwork,
  type TracePoint,
  type TracePointsBatch,
  type TraceSink,
  type TraceStage,
} from './types'

/**
 * Трассировка продажи в браузере (RP-SALE-TRACE, шаг 2).
 *
 * Два уровня:
 * - **лёгкий — всегда**: контрольные точки продажи (`mark`) копятся в памяти и уходят ОДНИМ запросом, когда продажа завершена
 *   (`done`), либо пакетом по {@link MAX_POINTS_PER_REQUEST} точек;
 * - **подробный — по включению** (`setDeep`): сверх того пишутся события запросов, очереди, опросов и живого канала.
 *   Выключен — события не собираются вовсе (а не собираются и отбрасываются).
 *
 * Главное правило: **трассировка никогда не влияет на продажу и не показывается кассиру.** Каждая публичная операция не бросает
 * исключений, отправка идёт фоном и не ждётся, сбой — тихий. Не дошедший пакет ждёт в очереди `sessionStorage` и уходит при
 * возврате связи; счётчик пропусков (`skipped`) виден в консоли только в подробном режиме.
 */

export interface MarkOptions {
  /** Кому принадлежит продажа для приёмника (в приложении — кабинет). Достаточно указать в первой метке продажи. */
  scope?: string
  saleId: string
  /** Ключ попытки оплаты; последний указанный уходит вместе с пакетом. */
  attemptKey?: string | null
  durationMs?: number | null
  outcome?: string | null
  meta?: TraceMeta | null
  /** Момент, мс от эпохи; по умолчанию — сейчас. */
  at?: number
}

export interface DetailOptions {
  method?: string | null
  at?: number
  durationMs?: number | null
  status?: number | null
  attempt?: number | null
  bytes?: number | null
}

export interface SaleTraceOptions {
  sink: TraceSink
  /** Хранилище очереди недоставленных пакетов (срок `session`); `null` — очереди нет, недоставленное пропадает. */
  storage?: KeyValueStorage | null
  /** Пространство ключа в хранилище. */
  namespace?: string
  now?: () => number
  /** Сеть кассира; по умолчанию — `navigator.connection`. */
  readNetwork?: () => TraceNetwork | null
  /** Предел очереди недоставленных пакетов. */
  queueLimit?: number
  /** Сколько живёт недоставленный пакет, мс. */
  queueTtlMs?: number
  /** Не чаще одной попытки доставить очередь за столько, мс. */
  retryGapMs?: number
  /** Потолок событий подробного режима на одну продажу. */
  detailLimit?: number
  /** Куда писать диагностику самой трассировки в подробном режиме. */
  log?: (message: string) => void
}

export interface SaleTrace {
  /** Разрешить или запретить трассировку для `scope` (у человека есть право — разрешить). По умолчанию запрещено: нет права — нет сбора. */
  allow: (scope: string, allowed: boolean) => void
  mark: (stage: TraceStage, options: MarkOptions) => void
  /** Подробный режим включён/выключен (флаг приходит с сервера). */
  setDeep: (enabled: boolean) => void
  isDeep: () => boolean
  /** Событие подробного режима, не связанное с HTTP (очередь корзины, опрос, живой канал). */
  detail: (kind: Exclude<TraceDetailKind, 'http'>, route: string, options?: DetailOptions) => void
  /** Хронометраж запроса от transport. */
  observeRequest: (timing: RequestTiming) => void
  /** Отправить накопленное по продаже сейчас (продажа брошена, окно закрыто). */
  flush: (saleId: string) => Promise<void>
  /** Попробовать доставить очередь недоставленного. */
  retryQueued: () => Promise<void>
  /** Сколько пакетов и событий пропущено из-за сбоев и потолков. */
  skipped: () => number
  /** Сколько пакетов ждёт в очереди. */
  queued: () => number
}

interface SaleRecord {
  scope: string
  attemptKey: string | null
  points: TracePoint[]
  events: TraceDetailEvent[]
}

interface QueuedBatch {
  id: string
  createdAt: number
  batch: { kind: 'points'; value: TracePointsBatch } | { kind: 'detail'; value: TraceDetailBatch }
}

const DEFAULT_QUEUE_LIMIT = 20
const DEFAULT_QUEUE_TTL_MS = 2 * 60 * 60 * 1000
const DEFAULT_RETRY_GAP_MS = 15_000
const DEFAULT_DETAIL_LIMIT = 1000
const QUEUE_KEY_PART = 'trace-queue:v1'

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function fieldOf(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>)[key] : undefined
}

/** Сеть кассира из `navigator.connection`; в браузере без интерфейса (Safari) — `null`, а не выдуманные цифры. */
export function readBrowserNetwork(): TraceNetwork | null {
  const connection = fieldOf(globalThis.navigator, 'connection')
  if (connection === undefined || connection === null) return null
  const type = fieldOf(connection, 'effectiveType')
  const effectiveType = typeof type === 'string' ? type : null
  const rttMs = numberOrNull(fieldOf(connection, 'rtt'))
  const downlinkMbps = numberOrNull(fieldOf(connection, 'downlink'))
  return effectiveType === null && rttMs === null && downlinkMbps === null ? null : { effectiveType, rttMs, downlinkMbps }
}

export function createSaleTrace(options: SaleTraceOptions): SaleTrace {
  const { sink } = options
  const storage = options.storage ?? null
  const queueKey = `${options.namespace ?? 'krp'}:${QUEUE_KEY_PART}`
  const now = options.now ?? Date.now
  const readNetwork = options.readNetwork ?? readBrowserNetwork
  const queueLimit = options.queueLimit ?? DEFAULT_QUEUE_LIMIT
  const queueTtlMs = options.queueTtlMs ?? DEFAULT_QUEUE_TTL_MS
  const retryGapMs = options.retryGapMs ?? DEFAULT_RETRY_GAP_MS
  const detailLimit = options.detailLimit ?? DEFAULT_DETAIL_LIMIT
  const log = options.log ?? ((message: string) => console.debug(message))

  const allowedScopes = new Set<string>()
  const sales = new Map<string, SaleRecord>()
  /** Продажа, к которой относятся события подробного режима: последняя, получившая метку и ещё не отправленная. */
  let activeSale: string | null = null
  let deep = false
  let skippedCount = 0
  let sequence = 0
  let lastRetryAt = Number.NEGATIVE_INFINITY
  const inFlight = new Set<string>()
  let queue: QueuedBatch[] | null = null

  const skip = (reason: string, count = 1) => {
    skippedCount += count
    if (deep) log(`Трассировка продажи: пропущено — ${reason}`)
  }

  const isoOf = (ms: number): string => new Date(ms).toISOString()

  // --- очередь недоставленных пакетов --------------------------------------------------------

  function loadQueue(): QueuedBatch[] {
    if (queue !== null) return queue
    queue = []
    if (storage === null) return queue
    try {
      const raw = storage.get(queueKey)
      if (raw === null) return queue
      const parsed: unknown = JSON.parse(raw)
      if (!Array.isArray(parsed)) return queue
      const items: unknown[] = parsed
      const fresh = now() - queueTtlMs
      for (const item of items) {
        if (!isQueued(item) || item.createdAt < fresh || queue.some((known) => known.id === item.id)) continue
        queue.push(item)
      }
    } catch {
      queue = []
    }
    return queue
  }

  function saveQueue(): void {
    if (storage === null || queue === null) return
    if (queue.length === 0) {
      storage.remove(queueKey)
      return
    }
    // Переполнение хранилища не повод ронять продажу: пакет останется только в памяти до перезагрузки.
    storage.set(queueKey, JSON.stringify(queue))
  }

  function enqueue(item: QueuedBatch): void {
    const list = loadQueue()
    // Один пакет — одна запись: повторная постановка того же id ничего не удваивает.
    if (list.some((known) => known.id === item.id)) return
    list.push(item)
    while (list.length > queueLimit) {
      list.shift()
      skip('очередь недоставленного переполнена, старейший пакет отброшен')
    }
    saveQueue()
  }

  function dequeue(id: string): void {
    const list = loadQueue()
    const index = list.findIndex((known) => known.id === id)
    if (index < 0) return
    list.splice(index, 1)
    saveQueue()
  }

  // --- отправка ------------------------------------------------------------------------------

  async function deliver(item: QueuedBatch): Promise<void> {
    // Пакет, который уже летит, второй раз не отправляется: иначе повтор по возврату связи удвоил бы точки.
    if (inFlight.has(item.id)) return
    inFlight.add(item.id)
    try {
      let outcome: TraceDelivery
      try {
        outcome =
          item.batch.kind === 'points' ? await sink.sendPoints(item.batch.value) : await sink.sendDetail(item.batch.value)
      } catch {
        // Приёмник обязан возвращать итог, а не бросать; бросил — пакет не вернуть, но и продажа не страдает.
        outcome = 'drop'
        skip('приёмник трассировки вызвал исключение')
      }
      if (outcome === 'retry') enqueue(item)
      else {
        dequeue(item.id)
        if (outcome === 'drop') skip('сервер отклонил пакет трассировки')
      }
    } finally {
      inFlight.delete(item.id)
    }
  }

  function nextId(saleId: string, kind: string): string {
    sequence += 1
    return `${saleId}:${kind}:${now()}:${sequence}`
  }

  function chunk<T>(items: readonly T[], size: number): T[][] {
    const chunks: T[][] = []
    for (let index = 0; index < items.length; index += size) chunks.push(items.slice(index, index + size))
    return chunks
  }

  /** Отправить пакеты продажи фоном: результат не ждётся. */
  function dispatchSale(saleId: string, record: SaleRecord): Promise<void> {
    const sends: Promise<void>[] = []
    if (record.points.length > 0) {
      const network = readNetwork()
      for (const points of chunk(record.points, MAX_POINTS_PER_REQUEST)) {
        const value: TracePointsBatch = { scope: record.scope, saleId, attemptKey: record.attemptKey, points, network }
        sends.push(deliver({ id: nextId(saleId, 'points'), createdAt: now(), batch: { kind: 'points', value } }))
      }
    }
    if (record.events.length > 0) {
      for (const events of chunk(record.events, MAX_DETAIL_PER_REQUEST)) {
        const value: TraceDetailBatch = { scope: record.scope, saleId, events }
        sends.push(deliver({ id: nextId(saleId, 'detail'), createdAt: now(), batch: { kind: 'detail', value } }))
      }
    }
    return Promise.all(sends).then(() => undefined)
  }

  function takeSale(saleId: string): SaleRecord | undefined {
    const record = sales.get(saleId)
    if (record === undefined) return undefined
    sales.delete(saleId)
    if (activeSale === saleId) activeSale = null
    return record
  }

  // --- публичное -----------------------------------------------------------------------------

  const safely = (what: string, action: () => void) => {
    try {
      action()
    } catch {
      skip(what)
    }
  }

  return {
    allow(scope, allowed) {
      safely('allow', () => {
        if (allowed) allowedScopes.add(scope)
        else allowedScopes.delete(scope)
      })
    },

    mark(stage, markOptions) {
      safely(`метка ${stage}`, () => {
        let record = sales.get(markOptions.saleId)
        const scope = markOptions.scope ?? record?.scope
        if (scope === undefined || !allowedScopes.has(scope)) return
        if (record === undefined) {
          record = { scope, attemptKey: null, points: [], events: [] }
          sales.set(markOptions.saleId, record)
        }
        if (markOptions.attemptKey !== undefined && markOptions.attemptKey !== null) record.attemptKey = markOptions.attemptKey
        activeSale = markOptions.saleId
        const meta = markOptions.meta ?? null
        record.points.push({
          stage,
          at: isoOf(markOptions.at ?? now()),
          durationMs: markOptions.durationMs ?? null,
          outcome: markOptions.outcome ?? null,
          meta: meta !== null && Object.keys(meta).length > 0 ? { ...meta } : null,
        })
        const finished = stage === 'done'
        if (finished || record.points.length >= MAX_POINTS_PER_REQUEST) {
          if (finished) {
            const taken = takeSale(markOptions.saleId)
            if (taken !== undefined) void dispatchSale(markOptions.saleId, taken).catch(() => skip('отправка'))
          } else {
            // Пакет полон: отправляем накопленные точки, продажа продолжается и копит дальше.
            const full: SaleRecord = { ...record, points: record.points, events: [] }
            record.points = []
            void dispatchSale(markOptions.saleId, full).catch(() => skip('отправка'))
          }
        }
      })
    },

    setDeep(enabled) {
      safely('setDeep', () => {
        deep = enabled
      })
    },

    isDeep: () => deep,

    detail(kind, route, detailOptions = {}) {
      safely(`событие ${kind}`, () => {
        if (!deep || activeSale === null || isTraceRoute(route)) return
        const record = sales.get(activeSale)
        if (record === undefined) return
        if (record.events.length >= detailLimit) {
          skip('потолок событий подробного режима на продажу')
          return
        }
        record.events.push({
          kind,
          route,
          method: detailOptions.method ?? null,
          at: isoOf(detailOptions.at ?? now()),
          durationMs: detailOptions.durationMs ?? null,
          status: detailOptions.status ?? null,
          attempt: detailOptions.attempt ?? null,
          bytes: detailOptions.bytes ?? null,
        })
      })
    },

    observeRequest(timing) {
      safely('запрос', () => {
        if (!deep || activeSale === null) return
        const route = routeTemplateOf(timing.path)
        if (isTraceRoute(route)) return
        const record = sales.get(activeSale)
        if (record === undefined) return
        if (record.events.length >= detailLimit) {
          skip('потолок событий подробного режима на продажу')
          return
        }
        record.events.push({
          kind: 'http',
          route,
          method: timing.method,
          at: isoOf(timing.startedAt),
          durationMs: timing.durationMs,
          status: timing.status,
          attempt: timing.attempts,
          bytes: timing.bytes,
        })
      })
    },

    async flush(saleId) {
      try {
        const taken = takeSale(saleId)
        if (taken !== undefined) await dispatchSale(saleId, taken)
      } catch {
        skip('отправка')
      }
    },

    async retryQueued() {
      try {
        const moment = now()
        if (moment - lastRetryAt < retryGapMs) return
        const fresh = moment - queueTtlMs
        const list = loadQueue()
        const expired = list.filter((item) => item.createdAt < fresh)
        if (expired.length > 0) {
          queue = list.filter((item) => item.createdAt >= fresh)
          saveQueue()
          skip('пакет в очереди устарел', expired.length)
        }
        const waiting = loadQueue().filter((item) => !inFlight.has(item.id))
        if (waiting.length === 0) return
        lastRetryAt = moment
        for (const item of waiting) await deliver(item)
      } catch {
        skip('повтор очереди')
      }
    },

    skipped: () => skippedCount,
    queued: () => loadQueue().length,
  }
}

function isQueued(value: unknown): value is QueuedBatch {
  if (typeof fieldOf(value, 'id') !== 'string' || typeof fieldOf(value, 'createdAt') !== 'number') return false
  const batch = fieldOf(value, 'batch')
  const kind = fieldOf(batch, 'kind')
  const payload = fieldOf(batch, 'value')
  return (kind === 'points' || kind === 'detail') && typeof payload === 'object' && payload !== null
}
